import { Form, Input, InputNumber, Select } from 'antd';
import { Rule } from 'antd/es/form';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { convertStudentRecordToFormData } from 'src/app/forms/StudentForm/student-form.config';
import { IFormProps } from 'src/app/forms/form.config';
import { getEnumSelectOptions } from 'src/app/helpers/enum.helper';
import {
  useCreateStudentMutation,
  useGetStudentsQuery,
  useUpdateStudentMutation,
} from 'src/app/redux/students/studentsSlice';
import { StudentRank } from 'src/enums/student.enum';

const yearRule: Rule[] = [
  { required: true },
  (formInstance) => ({
    validator(rule: any, value: number) {
      if (rule.field === 'graduationYear' && formInstance.getFieldValue('startYear')) {
        if (Number(formInstance.getFieldValue('startYear')) >= Number(value))
          return Promise.reject(new Error('Graduation Year must be greater than Start Year'));
      }
      if (value && value < 1900) {
        return Promise.reject(new Error(t(`errors.enterValidYearNumber`)));
      }
      if (!value || value?.toString()?.length === 4) {
        return Promise.resolve();
      }

      return Promise.reject(new Error(t(`errors.mustBeFourCharacters`)));
    },
  }),
];

const StudentForm: React.FC<IFormProps> = ({ form, recordId }) => {
  const [createStudent] = useCreateStudentMutation();
  const [updateStudent] = useUpdateStudentMutation();

  const { data: editData } = useGetStudentsQuery(undefined, {
    skip: !recordId,
    selectFromResult: ({ data }) => ({
      data: data?.rows.find((row) => row.id === recordId),
    }),
  });

  const onFinish = async (values: any) => {
    if (!recordId) {
      createStudent(values);
    } else {
      updateStudent({
        id: recordId,
        ...values,
      });
    }
  };

  useEffect(() => {
    if (editData) {
      form.setFieldsValue(convertStudentRecordToFormData(editData));
    }
  }, [editData, form]);

  return (
    <FormComponent
      options={{
        form,
        onFinish,
      }}
    >
      <Form.Item label={t('forms.studentForm.studentName')} name="studentName">
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.qid')} name="qId" rules={[{ len: 11 }]}>
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.rank')} name="rank">
        <SearchableSelect
          variant="borderless"
          options={getEnumSelectOptions(StudentRank).map((opt) => ({
            ...opt,
            label: t(`studentsPage.ranks.${opt.value}`),
          }))}
          // options={[
          //   {
          //     label: 'ملازم أول - Second Lieutenant',
          //     value: 'ملازم أول - Second Lieutenant',
          //   },
          //   {
          //     label: 'ملازم - Lieutenant',
          //     value: 'ملازم - Lieutenant',
          //   },
          //   { label: 'نقيب - Captain', value: 'نقيب - Captain' },
          //   { label: 'رئيسي - Major', value: 'رئيسي - Major' },
          //   {
          //     label: 'مقدم - Lieutenant Colonel',
          //     value: 'مقدم - Lieutenant Colonel',
          //   },
          //   { label: 'عقيد - Colonel', value: 'عقيد - Colonel' },
          //   { label: 'مدني - Civilian', value: 'مدني - Civilian' },
          // ]}
        />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.branch')} name="branch">
        <Input bordered={false} />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.militaryId')} name="militaryId" rules={[{ required: true }]}>
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.universityName')} name="universityName">
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.universityCountry')} name="universityCountry">
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.universityCity')} name="universityCity">
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.typeOfDegree')} name="degreeType">
        <SearchableSelect
          variant="borderless"
          options={[
            { label: 'Bachelor', value: 'Bachelor' },
            { label: 'Ph.D.', value: 'Ph.D.' },
            { label: 'Master', value: 'Master' },
          ]}
        />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.program')} name="program">
        <Input bordered={false} />
      </Form.Item>
      <Form.Item
        label={t('forms.studentForm.yearOfProgram')}
        name="yearOfProgram"
        rules={[
          {
            validator(rule, value) {
              if (!value || value?.toString().length === 1) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Year of program must be 1 character'));
            },
          },
        ]}
      >
        <InputNumber variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.startYear')} name="startYear" rules={[yearRule[1]]}>
        <InputNumber bordered={false} />
      </Form.Item>
      <Form.Item
        label={t('forms.studentForm.expectedGraduationYear')}
        name="expectedGraduationYear"
        rules={[yearRule[1]]}
      >
        <InputNumber variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.graduationYear')} name="graduationYear" rules={[yearRule[1]]}>
        <InputNumber variant="borderless" />
      </Form.Item>
      <Form.Item label={t('forms.studentForm.graduationApproved')} name="graduationApproved">
        <Select
          bordered={false}
          options={[
            { label: 'Approved', value: true },
            { label: 'Not Yet', value: false },
          ]}
        />
      </Form.Item>
    </FormComponent>
  );
};

export default StudentForm;
